'use client';

import { supabaseBrowser } from '../supabaseBrowser';

const supabase = supabaseBrowser;

// Live updates from the database (migration 20261015160000_live_number_grid).
// The database announces every change itself, on private channels that
// anyone can listen to and nobody can send on:
//   comp:<competition id>  event 'grid'    { sold: [n], held: [{ n, e }], free: [n], at }
//   comp:counts            event 'counts'  { counts: [{ id, sold, total }] }
// No player ids are ever sent.

// ---------------------------------------------------------------------------
// Shared channels: one per topic for the whole tab, however many components
// listen. A channel closes 10 seconds after its last listener leaves, so a
// page change or a log-in (unmount, then mount again) reuses it instead of
// rejoining a channel that is still closing.
// ---------------------------------------------------------------------------

const channels = new Map(); // topic -> { channel, listeners, status, stopTimer }

function listen(topic, event, listener) {
  if (typeof window === 'undefined' || !supabase) return () => {};
  let entry = channels.get(topic);
  if (!entry) {
    entry = { channel: null, listeners: new Set(), status: null, stopTimer: null };
    channels.set(topic, entry);
    const current = entry;
    current.channel = supabase
      .channel(topic, { config: { private: true } })
      .on('broadcast', { event }, (msg) => {
        current.listeners.forEach((l) => l.onMessage?.(msg?.payload || {}));
      })
      .subscribe((status) => {
        current.status = status;
        current.listeners.forEach((l) => l.onStatus?.(status));
      });
  }
  clearTimeout(entry.stopTimer);
  entry.stopTimer = null;
  entry.listeners.add(listener);
  // Joined already: let the newcomer catch up straight away.
  if (entry.status === 'SUBSCRIBED') listener.onStatus?.('SUBSCRIBED');

  const mine = entry;
  return () => {
    mine.listeners.delete(listener);
    if (mine.listeners.size > 0 || mine.stopTimer) return;
    mine.stopTimer = setTimeout(() => {
      if (mine.listeners.size > 0) return;
      channels.delete(topic);
      supabase.removeChannel(mine.channel);
    }, 10_000);
  };
}

/**
 * Listen to one competition's number grid. `onGrid(payload)` gets each
 * change; `onStatus(status)` gets the channel status ('SUBSCRIBED' after
 * every connect or reconnect, so the caller can catch up on anything missed).
 * Returns an unsubscribe function.
 */
export function subscribeToGrid(competitionId, onGrid, onStatus) {
  if (!competitionId) return () => {};
  return listen(`comp:${competitionId}`, 'grid', { onMessage: onGrid, onStatus });
}

// ---------------------------------------------------------------------------
// Sold counts: the shared comp:counts channel, plus one batched self-heal
// read a minute for every counter on the page.
// ---------------------------------------------------------------------------

const countListeners = new Map(); // competition id -> Set of callbacks
let stopCountsChannel = null;
let healTimer = null;
let refetchTimer = null;

function dispatchCount(id, sold) {
  if (typeof sold !== 'number') return;
  countListeners.get(id)?.forEach((cb) => cb(sold));
}

async function refetchCounts() {
  const ids = Array.from(countListeners.keys());
  if (!ids.length) return;
  const { data } = await supabase.from('competitions').select('id, tickets_sold').in('id', ids);
  (data || []).forEach((row) => dispatchCount(row.id, row.tickets_sold));
}

// Many counters mount at once on a listings page: one read for all of them.
function scheduleRefetch() {
  if (refetchTimer) return;
  refetchTimer = setTimeout(() => {
    refetchTimer = null;
    refetchCounts().catch(() => {});
  }, 50);
}

function onVisible() {
  if (document.visibilityState === 'visible') scheduleRefetch();
}

function startCounts() {
  stopCountsChannel = listen('comp:counts', 'counts', {
    onMessage: (payload) => (payload?.counts || []).forEach((c) => dispatchCount(c?.id, c?.sold)),
    // Catch up after every (re)connect.
    onStatus: (status) => {
      if (status === 'SUBSCRIBED') scheduleRefetch();
    },
  });
  healTimer = setInterval(scheduleRefetch, 60_000);
  document.addEventListener('visibilitychange', onVisible);
}

function stopCounts() {
  stopCountsChannel?.();
  stopCountsChannel = null;
  clearInterval(healTimer);
  healTimer = null;
  document.removeEventListener('visibilitychange', onVisible);
}

/**
 * Listen to one competition's tickets-sold count. `onSold(count)` gets each
 * change. Returns an unsubscribe function.
 */
export function subscribeToSoldCount(competitionId, onSold) {
  if (typeof window === 'undefined' || !competitionId || !supabase) return () => {};
  if (!countListeners.has(competitionId)) countListeners.set(competitionId, new Set());
  countListeners.get(competitionId).add(onSold);
  if (!stopCountsChannel) startCounts();
  // A server-rendered count may be stale (cached page): read it now.
  scheduleRefetch();

  return () => {
    const set = countListeners.get(competitionId);
    set?.delete(onSold);
    if (set && set.size === 0) countListeners.delete(competitionId);
    if (countListeners.size === 0) stopCounts();
  };
}
